import React, { useState, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useNavigate, useLocation } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import TableActions from './TableActions';
import Button from '../Button';
import { convertUTCToLocal } from '../../utils/dateUtils';
import 'primereact/resources/themes/saga-blue/theme.css';

// helper function to check if a value is an iso date string
const isISODateString = (value) => {
  if (typeof value !== 'string') return false;
  // match iso 8601 date format
  const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
  return isoDateRegex.test(value);
};

// helper function to get nested property value
const getNestedValue = (obj, path) => {
  if (!path) return undefined;
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};
// import "primereact/resources/primereact.min.css";
// import "primeicons/primeicons.css";
// import "primeflex/primeflex.css";

const DataTableComponent = ({
  data,
  columns,
  DeleteModalToast,
  loading,
  paginationParams, // Optional: Pass this if pagination is needed
  totalRecords, // Optional: Pass this if pagination is needed
  onDataTableEvent, // Optional: Pass this if pagination or lazy loading is needed
  enableCreate = false,
  enableUpdate = false,
  enableDelete = false,
  enableExport = false,
  enablePagination = false, // Default: false
  enableLazyLoading = false, // Default: false
  actions = [],
  actionsHeader = 'Actions',
  onRowClick,
  children,
  sectionName,
  enableSelection = false,
  createPath, // consumed here, do not forward to DOM
  ...rest
}) => {
  const dt = useRef(null);
  const navigate = useNavigate();
  // const [selectedRows, setSelectedRows] = useState([]);
  const [showSubComponent, setShowSubComponent] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const location = useLocation();

  // custom csv export function that formats dates properly
  const exportCSV = () => {
    if (!data || data.length === 0) return;

    // get column headers and fields
    const exportColumns = columns.filter((col) => col.field);

    // create csv header row
    const headers = exportColumns.map((col) => col.header || col.field);

    // create csv data rows with formatted dates
    const rows = data.map((row) => {
      return exportColumns.map((col) => {
        let value = getNestedValue(row, col.field);

        // format iso date strings to local format
        if (isISODateString(value)) {
          value = convertUTCToLocal(value);
        }

        // escape special characters for csv
        if (typeof value === 'string') {
          // escape quotes and wrap in quotes if contains comma, newline, or quote
          if (
            value.includes(',') ||
            value.includes('\n') ||
            value.includes('"')
          ) {
            value = `"${value.replace(/"/g, '""')}"`;
          }
        }

        return value ?? '';
      });
    });

    // combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.join(',')),
    ].join('\n');

    // create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const filename = sectionName ? `${sectionName}.csv` : 'Data.csv';

    if (navigator.msSaveBlob) {
      // ie 10+
      navigator.msSaveBlob(blob, filename);
    } else {
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // const showDeleteModal = (rowData) => {
  //   setSelectedData([{ id: rowData.id }]);
  //   setShowSubComponent(true);
  // };

  return (
    <div>
      <div
        style={{
          justifyContent: 'right',
          display: 'flex',
          marginBottom: '30px',
        }}
      >
        {enableCreate && (
          <Button
            color="primary"
            text={`Create New`}
            style={{
              width: '136px',
              minHeight: '32px',
              marginRight: '35px',
              fontSize: '12px',
            }}
            onClick={() => {
              navigate(createPath ?? 'create');
            }}
          />
        )}
        {enableExport && (
          <Button
            color="primary"
            text={`Export CSV`}
            style={{ width: '136px', minHeight: '32px', fontSize: '12px' }}
            onClick={() => exportCSV()}
          />
        )}
      </div>

      {loading ? (
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <DataTable
          style={{ fontSize: '12px', fontFamily: 'Proxima Nova' }}
          ref={dt}
          exportFilename={sectionName ? sectionName : 'Data'}
          value={data}
          lazy={enableLazyLoading} // Enable lazy loading only if prop is true
          paginator={enablePagination} // Enable pagination only if prop is true
          rows={paginationParams?.limit || 10} // Use paginationParams.limit if provided
          totalRecords={totalRecords} // Required for pagination
          first={paginationParams?.offset || 0} // Use paginationParams.offset if provided
          onPage={enablePagination ? onDataTableEvent : undefined} // Only enable if pagination is enabled
          onSort={enableLazyLoading ? onDataTableEvent : undefined} // Only enable if lazy loading is enabled
          sortField={paginationParams?.sortField}
          sortOrder={paginationParams?.sortOrder}
          filters={paginationParams?.filters}
          rowsPerPageOptions={enablePagination ? [5, 10, 20] : undefined} // Only show if pagination is enabled
          responsiveLayout="scroll"
          onRowClick={onRowClick}
          {...rest}
        >
          {enableSelection && (
            <Column
              selectionMode="multiple"
              headerStyle={{ width: '3rem' }}
            ></Column>
          )}
          {columns.map((col, i) => (
            <Column
              key={i}
              field={col.field}
              header={col.header}
              sortable={col.sortable}
              filter={col.filter}
              body={col.body}
              {...col.columnProps}
              headerStyle={{ padding: '0.5rem' }}
            />
          ))}

          {actions.length > 0 && (
            <Column
              header={actionsHeader}
              body={(row) => {
                // Filter actions based on the `visible` property
                const filteredActions = actions.filter((action) =>
                  action.visible ? action.visible(row) : true
                );
                return <TableActions actions={filteredActions} row={row} />;
              }}
              style={{ width: '180px' }}
            />
          )}

          {/* {actions.length > 0 && <Column header="Actions" body={(row) => <TableActions actions={actions} row={row} />} style={{ width: "180px" }} />} */}
        </DataTable>
      )}

      {showSubComponent &&
        DeleteModalToast &&
        React.createElement(DeleteModalToast, {
          sentData: selectedData,
          onEvent: () => {},
          onConfirm: () => setShowSubComponent(false),
        })}

      {children}
    </div>
  );
};

export default DataTableComponent;
